import { FC, useMemo } from 'react';
import { Box, FormHelperText, List, ListItem, Stack, Theme } from '@mui/material';
import { FormProps } from '../../helpers';
import { createUseStyles } from 'react-jss';
import { PublicServiceConfig } from '@/lib/models/policy-model-v1/public-services';
import useAppData from '@/hooks/use-app-data.hook';
import { AppDataQueryKey } from '@/lib/query-client';
import { PublicServiceMeta } from '@/lib/models/public-service/public-service.model';
import { getListedNewServices } from '@/lib/models/public-service';
import NewServiceActionSelectV1 from './select';
import { PolicyModelV1 } from '@/lib/models/policy-model-v1/policy-v1.model';
import { ServiceAction, getServiceAction, applyServiceAction } from './helpers';
import ServiceLink from '@/components/service-link';
import Chip from '@/components/chip';
import Text from '@/components/text';

interface Props extends FormProps {
  value: PublicServiceConfig;
  onChange: (value: PublicServiceConfig) => void;
  policy: PolicyModelV1;
}

const useStyles = createUseStyles<string, { newServiceCount: number }>((theme: Theme) => ({
  listEditor: {
    border: `1px solid ${theme.palette.gray}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.common.white,
    '& ul': {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: ({ newServiceCount }) => (newServiceCount > 2 ? 'flex-start' : 'center'),
      height: 'auto',
      minHeight: 120,
      maxHeight: 160,
      overflowY: 'auto',
      backgroundColor: theme.palette.common.white,
      '&.MuiList-root': {
        paddingTop: 0,
        paddingBottom: 0,
      },
    },
    '& .MuiFormControl-root': {
      padding: 0,
      margin: 0,
    },
    '& li.MuiListItem-root': {
      paddingTop: 4,
      paddingBottom: 0,
      backgroundColor: theme.palette.common.white,
    },
  },
  listItem: {
    padding: '2px 0px',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    gap: 8,
  },
  header: {
    padding: 10,
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.common.white,
    '& .MuiInputBase-root': {
      backgroundColor: theme.palette.common.white,
    },
  },
  selectWrapper: {
    width: '30%',
  },
  list: {
    borderRadius: theme.shape.borderRadius,
  },
}));

const NewServicesListV1: FC<Props> = ({ value, policy, onChange, ...rest }) => {
  const newServiceIds = policy.newPublicServices;

  const selectedActions = useMemo<Record<string, ServiceAction>>(() => {
    const actions: Record<string, ServiceAction> = {};
    newServiceIds.forEach((serviceId) => {
      actions[serviceId] = getServiceAction(serviceId, value);
    });
    return actions;
  }, [value, newServiceIds]);

  const query = useAppData<PublicServiceMeta[]>(AppDataQueryKey.PublicServices);
  const { data = [] } = query;

  const newServices = useMemo(
    () => getListedNewServices(data, newServiceIds),
    [data, newServiceIds]
  );

  const newServiceCount = newServices.length;

  const styles = useStyles({ newServiceCount });
  const handleSelectChange = (serviceId: string, action: ServiceAction) => {
    const updated = applyServiceAction(value, serviceId, action);
    onChange(updated);
  };

  const hasError = !!rest.error;

  return (
    <Stack>
      <Box mb={2}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Text size="small" color="white" bold>
            Review and acknowledge each new service by selecting the action you'd like to apply.
          </Text>
          <Chip label={`${newServiceCount} new services`} color="primary" size="small" />
        </Stack>
      </Box>
      <Stack className={styles.listEditor}>
        <List className={styles.list}>
          {newServices.map((service) => {
            const { id: serviceId } = service;
            const selectedAction = selectedActions[serviceId];

            return (
              <ListItem key={serviceId}>
                <Stack className={styles.listItem}>
                  <Box flexGrow={1}>
                    <ServiceLink service={service} variant="outlined" />
                  </Box>
                  <Box className={styles.selectWrapper}>
                    <NewServiceActionSelectV1
                      value={selectedAction || ''}
                      onChange={(action: ServiceAction) => handleSelectChange(serviceId, action)}
                      selectedAction={selectedAction}
                    />
                  </Box>
                </Stack>
              </ListItem>
            );
          })}
        </List>
        {hasError && <FormHelperText error>{rest.error}</FormHelperText>}
      </Stack>
    </Stack>
  );
};

export default NewServicesListV1;
